import * as React from "react";
import {
  Link,
  Redirect,
  RouteComponentProps,
  withRouter,
} from "react-router-dom";
import {
  MandatoryConsentCheckboxes,
  RouterPaths,
  TranslationKeys,
} from "../../../infrastructure/const";
import CheckUtils from "../../../utilities/check-utils";
import TranslationsContext from "../../../infrastructure/context/translations/translations-context";
import CheckoutBatterySummary from "./checkout-battery-summary";
import IUserContext from "../../../infrastructure/context/user/user.context.interface";
import UserContext from "../../../infrastructure/context/user/user.context";
import {
  Address,
  UpdateCartSimpleTransactionRequest,
  UpdateCartPropertiesRequest,
  InitializeTransactionResponse,
} from "../../../infrastructure/http/modules/cart/cart.models";
import CheckoutAddress from "./checkout-battery-address";
import TranslationManager from "../../../infrastructure/translation-manager";
import ITranslationsContext from "../../../infrastructure/context/translations/translations-context.interface";
import { GetHttpClientInstance } from "../../../infrastructure/context/http/http-context-provider";
import { useContext, Ref } from "react";
import CacheManager from "../../../infrastructure/cache/cache-manager";
import CacheKeys from "../../../infrastructure/cache/cache-keys";
import { AddEditAddressRequest } from "../../../infrastructure/http/modules/account/account.models";
import { UserProfileResponse } from "../../../infrastructure/http/modules/auth/auth.models";
import ReactHtmlParser from 'react-html-parser';

type CheckoutState = {
  deliveryAddress: Address | null;
  invoiceAddress: Address;
  isFilledCorrectly: boolean;
  displayValidationMessages: boolean;
  shippingMethod: string;
  isLoaded: boolean;
  apiErrorResponse: ErrorResponse;
  saveInvoiceAddress: boolean;
  saveDeliveryAddress: boolean;
  specialInformation: string;
  transactionInProgress: boolean;
  useCaptchaV2: boolean;
};

interface CheckoutProps extends RouteComponentProps {}

export type ErrorResponse = {
  invoiceAddressErrors: Array<string>;
  deliveryAddressErrors: Array<string>;
  addtionalErrors: Array<string>;
};

class BatteryCheckout extends React.Component<CheckoutProps, CheckoutState> {
  private addressRef: React.RefObject<CheckoutAddress>;

  constructor(props: CheckoutProps, context: IUserContext) {
    super(props, context);

    const invoiceAddressCached = CacheManager.getItem(
      CacheKeys.invoiceAddressKey
    ) as Address;
    const deliveryAddressCached = CacheManager.getItem(
      CacheKeys.deliveryAddressKey
    ) as Address;
    const specialInformation = sessionStorage.getItem(CacheKeys.specialInformation);

    this.state = {
      invoiceAddress: CheckUtils.isNullObject(invoiceAddressCached)
        ? new Address()
        : invoiceAddressCached,
      deliveryAddress: deliveryAddressCached,
      displayValidationMessages: false,
      isLoaded: true,
      shippingMethod: context.cart.selectedShippingType,
      apiErrorResponse: {} as ErrorResponse,
      saveInvoiceAddress: false,
      saveDeliveryAddress: false,
      specialInformation: specialInformation,
      transactionInProgress: false,
      useCaptchaV2: false
    } as CheckoutState;

    this.addressRef = React.createRef();
    this.setBinding();
  }

  render(): JSX.Element | null {
    return (
      <div className="checkout-onepage-index">
        <div className="col1-layout">
          <div className="col-main">
            <div>
              <div className="checkout-container wrapper">
                <CheckoutPageHeaderInner />
                <div id="checkoutSteps" className="one-page-checkout clearfix">
                  <ul className="section">
                    <AddressSectionInner
                      ref={this.addressRef}
                      displayValidationMessage={
                        this.state.displayValidationMessages
                      }
                      onDeliveryAddressChange={this.onDeliveryAddressChange}
                      onInvoiceAddressChange={this.onInvoiceAddressChange}
                      onSaveDeliveryAddressChange={
                        this.onSaveDeliveryAddressChange
                      }
                      onSaveInvoiceAddressChange={
                        this.onSaveInvoiceAddressChange
                      }
                      apiErrorCodes={this.state.apiErrorResponse}
                      invoiceAddress={this.state.invoiceAddress}
                      deliveryAddress={this.state.deliveryAddress}
                      specialInformation={this.state.specialInformation}
                      onSpecialInformationChange={this.onSpecialInformationChange}
                    />
                  </ul>
                  <>
                    <ul className="section">
                      <CheckoutSummaryInner
                        onContinueClick={this.onContinueClick}
                        isLoaded={this.state.isLoaded}
                        displayValidationMessage={
                          this.state.displayValidationMessages
                        }
                        invoiceAddressApiIsIncorrect={this.addressApiIsIncorrect(this.state.invoiceAddress)}
                        deliveryAddressApiIsIncorrect={this.addressApiIsIncorrect(this.state.deliveryAddress)}
                        apiErrorCodes={this.state.apiErrorResponse}
                        useCaptchaV2={this.state.useCaptchaV2}
                      />
                    </ul>
                  </>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  private addressApiIsIncorrect(address: Address | null): boolean {
    if(!address || !address.addressApiText) {
      return false;
    }

    return (!address.addressLine1 || !address.postalCode || !address.city || !address.countryCode || !address.county);
  }


  private onContinueClick(
    userId: string,
    cartHash: string,
    checkboxesSelected: Array<string>,
    areAllMandatoryCheckboxesSelected: boolean,
    captchaV2Token: string,
    captchaV3Token: string
  ): Redirect | void {
    if(this.state.transactionInProgress) {
      return;
    }

    this.setState({ apiErrorResponse: {} as ErrorResponse, transactionInProgress: true });

    const termsAndConditionsAgreed = checkboxesSelected.includes(
      MandatoryConsentCheckboxes.TermsAndConditions
    );

    // Validate
    if (!areAllMandatoryCheckboxesSelected || !this.isFilledCorrectly()) {
      this.setState({
        displayValidationMessages: true,
        transactionInProgress: false
      });
      return;
    }

    if (
      !CheckUtils.isNullObject(this.state.invoiceAddress) &&
      this.state.saveInvoiceAddress
    ) {
      const addAddressRequest = this.buildAddAddressRequest(
        this.state.invoiceAddress
      );
      this.saveAddress(addAddressRequest, this.context);
    }

    if (
      !CheckUtils.isNullObject(this.state.deliveryAddress) &&
      this.state.saveDeliveryAddress
    ) {
      const addAddressRequest = this.buildAddAddressRequest(
        this.state.deliveryAddress as Address
      );
      this.saveAddress(addAddressRequest, this.context);
    }

    const customProperties = {
      ...this.context.cart.customProperties,
      special_information: this.state.specialInformation
    };

    var request: UpdateCartSimpleTransactionRequest = {
      userId: userId,
      shippingMethod: this.state.shippingMethod,
      invoiceAddress: this.state.invoiceAddress,
      deliveryAddress: this.state.deliveryAddress,
      customProperties: customProperties,
      termsAndConditionsAgreed,
      privacyPolicyAgreed: false,
      captchaV2Token: captchaV2Token,
      captchaV3Token: captchaV3Token
    };

    GetHttpClientInstance()
      .cart.initializeSimpleTransaction(request)
      .then((result: InitializeTransactionResponse) => {
        if (this.isContinueResultSuccess(result)) {
          this.renderConfirmationRedirect(
            result.transactionId
          );
        } else {
          const errors: Array<string> = result.errorKeys;
          if(result.errorKeys.some(s=> s==='CaptchaV3Token')) {
            this.setState({
              useCaptchaV2: true,
              transactionInProgress: false
            });
          }else {
            this.setState({
              displayValidationMessages: true,
              apiErrorResponse: this.ConvertApiErrorResponse(errors),
              transactionInProgress: false
            });
          }
        }
      }).catch((error: any) => {
         this.setState({
            transactionInProgress: false
          });
      });
  }

  private onDeliveryAddressChange(deliveryAddress: Address | null): void {
    this.setState({ deliveryAddress });
    if (CheckUtils.isNullObject(deliveryAddress)) {
      CacheManager.removeItem(CacheKeys.deliveryAddressKey);
      this.setState({ saveDeliveryAddress: false });
    } else {
      CacheManager.setItem(
        CacheKeys.deliveryAddressKey,
        deliveryAddress as Address
      );
    }
  }

  private onSaveInvoiceAddressChange(saveInvoiceAddress: boolean): void {
    this.setState({ saveInvoiceAddress });
  }

  private onSaveDeliveryAddressChange(saveDeliveryAddress: boolean): void {
    this.setState({ saveDeliveryAddress });
  }

  private onInvoiceAddressChange(invoiceAddress: Address): void {
    this.setState({ invoiceAddress });
    CacheManager.setItem(CacheKeys.invoiceAddressKey, invoiceAddress);
  }

  private onSpecialInformationChange(specialInformation: string): void {
    this.setState({ specialInformation });
    sessionStorage.setItem(CacheKeys.specialInformation, specialInformation);
  }

  private isContinueResultSuccess(
    result: InitializeTransactionResponse
  ): boolean {
    return CheckUtils.isNullOrEmptyArray(result.errorKeys);
  }

  private isFilledCorrectly(): boolean {
    // Component ref is null (Component does not exist) or component does exist and is filled correctly
    const addressValid: boolean =
      CheckUtils.isNullObject(this.addressRef.current) ||
      this.addressRef.current!.isComponentFilledCorrectly();

    return addressValid;
  }

  private updateCart(userContext: IUserContext): void {
    this.setState({ isLoaded: false }, () => {
      const customProperties = {
        ...userContext.cart.customProperties,
      };
      GetHttpClientInstance()
        .cart.updateProperties({
          userId: userContext.user.userProfile.userId,
          customProperties,
          shippingMethod: this.state.shippingMethod,
        } as UpdateCartPropertiesRequest)
        .then(() => {
          userContext.refreshCart().then(() => {
            this.setState({ isLoaded: true });
          });
        });
    });
  }

  private ConvertApiErrorResponse(errorCodes: Array<string>): ErrorResponse {
    let response = {} as ErrorResponse;
    if (CheckUtils.isNullOrEmptyArray(errorCodes)) {
      return response;
    }
    const invoiceAddressErrors = errorCodes.filter(
      (x) =>
        x.toLowerCase().indexOf(CacheKeys.invoiceAddressKey.toLowerCase()) >= 0
    );
    const deliveryAddressErrors = errorCodes.filter(
      (x) =>
        x.toLowerCase().indexOf(CacheKeys.deliveryAddressKey.toLowerCase()) >= 0
    );

    response.invoiceAddressErrors = invoiceAddressErrors.map((error) => {
      const arr = error.split(".");
      return arr[arr.length - 1].toLowerCase();
    });
    response.deliveryAddressErrors = deliveryAddressErrors.map((error) => {
      const arr = error.split(".");
      return arr[arr.length - 1].toLowerCase();
    });

    response.addtionalErrors = errorCodes;

    return response;
  }

  private renderConfirmationRedirect = (
    transactionId: string,
  ) => {
    this.props.history.push(
      RouterPaths.OrderConfirmation + "/" + transactionId
    );
  };

  private setBinding() {
    this.onInvoiceAddressChange = this.onInvoiceAddressChange.bind(this);
    this.onDeliveryAddressChange = this.onDeliveryAddressChange.bind(this);
    this.onContinueClick = this.onContinueClick.bind(this);
    this.isFilledCorrectly = this.isFilledCorrectly.bind(this);
    this.isContinueResultSuccess = this.isContinueResultSuccess.bind(this);
    this.renderConfirmationRedirect = this.renderConfirmationRedirect.bind(this);
    this.updateCart = this.updateCart.bind(this);
    this.onSaveInvoiceAddressChange =
      this.onSaveInvoiceAddressChange.bind(this);
    this.onSaveDeliveryAddressChange =
      this.onSaveDeliveryAddressChange.bind(this);
    this.onSpecialInformationChange= this.onSpecialInformationChange.bind(this);
  }

  private buildAddAddressRequest(address: Address): AddEditAddressRequest {
    const addressRequest: AddEditAddressRequest = {
      firstName: address.firstName,
      lastName: address.lastName,
      telephone: address.phoneNumber,
      streetAddress1: address.addressLine1,
      streetAddress2: address.addressLine2,
      city: address.city,
      postalCode: address.postalCode,
      country: address.countryCode,
      isDefaultBillingAddress: false,
      isDefaultShippingAddress: false,
    };
    return addressRequest;
  }

  private saveAddress(
    addAddressRequest: AddEditAddressRequest,
    userContext: IUserContext
  ) {
    GetHttpClientInstance()
      .account.addAddress(addAddressRequest, {
        accessToken: userContext.user.tokenInfo!.accessToken,
      })
      .then((userProfileResponse: UserProfileResponse) => {
        userContext.updateUserProfile(userProfileResponse);
      });
  }
}

const CheckoutPageHeaderInner = (): JSX.Element => {
  const translationsContext =
    useContext<ITranslationsContext>(TranslationsContext);
  const translationManager: TranslationManager = new TranslationManager(
    translationsContext
  );
  const translations: { [id: string]: string } = {
    header: translationManager.getTranslation(TranslationKeys.CHECKOUT_HEADER),
    headerSecond: translationManager.getTranslation(TranslationKeys.CHECKOUT_HEADER_SECOND),
    backToCart: translationManager.getTranslation(
      TranslationKeys.CHECKOUT_BACKTOCART_BUTTON
    ),
  };
  return (
    <>
      <Link
        className="back-link"
        to={RouterPaths.CartPage}
        title={translations.backToCart}
      >
        {translations.backToCart}
      </Link>
      <h1 className="page-big-title">{translations.header}</h1>
      <p>{ReactHtmlParser(translations.headerSecond)}</p>
    </>
  );
};

const AddressSectionInner = React.forwardRef(
  (
    props: {
      displayValidationMessage: boolean;
      onDeliveryAddressChange: (address: Address | null) => void;
      onInvoiceAddressChange: (address: Address) => void;
      onSaveInvoiceAddressChange: (saveAddress: boolean) => void;
      onSaveDeliveryAddressChange: (saveAddress: boolean) => void;
      apiErrorCodes: ErrorResponse;
      invoiceAddress: Address;
      deliveryAddress: Address | null;
      specialInformation: string;
      onSpecialInformationChange: (specialInformation: string) => void;
    },
    ref: Ref<CheckoutAddress>
  ): JSX.Element => {
    const userContext = useContext<IUserContext>(UserContext);
    return (
      <CheckoutAddress
        ref={ref}
        userContext={userContext}
        displayValidationMessages={props.displayValidationMessage}
        apiErrorResponse={props.apiErrorCodes}
        onDeliveryAddressChange={(address: Address | null) =>
          props.onDeliveryAddressChange(address)
        }
        onInvoiceAddressChange={(address: Address) =>
          props.onInvoiceAddressChange(address)
        }
        onSaveDeliveryAddressChange={(saveAddress: boolean) =>
          props.onSaveDeliveryAddressChange(saveAddress)
        }
        onSaveInvoiceAddressChange={(saveAddress: boolean) =>
          props.onSaveInvoiceAddressChange(saveAddress)
        }
        invoiceAddress={props.invoiceAddress}
        deliveryAddress={props.deliveryAddress}
        specialInformation={props.specialInformation}
        onSpecialInformationChange={(specialInformation: string) =>
          props.onSpecialInformationChange(specialInformation)
        }
      />
    );
  }
);

const CheckoutSummaryInner = (props: {
  displayValidationMessage: boolean;
  onContinueClick: (
    userId: string,
    cartHash: string,
    checkboxesSelected: Array<string>,
    areAllMandatoryCheckboxesSelected: boolean,
    captchaV2Token: string,
    captchaV3Token: string
  ) => void;
  isLoaded: boolean;
  invoiceAddressApiIsIncorrect: boolean;
  deliveryAddressApiIsIncorrect: boolean;
  apiErrorCodes: ErrorResponse;
  useCaptchaV2: boolean;
}): JSX.Element => {
  const userContext = useContext<IUserContext>(UserContext);
  const cart = userContext.cart;

  return (
    <CheckoutBatterySummary
      onContinueClick={(
        checkboxesSelected: Array<string>,
        areAllMandatoryCheckboxesSelected: boolean,
        captchaV2Token: string,
        captchaV3Token: string
      ) =>
        props.onContinueClick(
          userContext.user.userProfile!.userId,
          cart.hash,
          checkboxesSelected,
          areAllMandatoryCheckboxesSelected,
          captchaV2Token,
          captchaV3Token
        )
      }
      isLoaded={props.isLoaded}
      displayValidationMessages={props.displayValidationMessage}
      invoiceAddressApiIsIncorrect={props.invoiceAddressApiIsIncorrect}
      deliveryAddressApiIsIncorrect={props.deliveryAddressApiIsIncorrect}
      apiErrorResponse={props.apiErrorCodes.addtionalErrors}
      showCaptchaV2={props.useCaptchaV2}
    />
  );
};

BatteryCheckout.contextType = UserContext;

export default withRouter(BatteryCheckout);
